import { useMemo } from 'react';
import type { ComponentType } from 'react';
import type { ColDef, ICellRendererParams, ValueFormatterParams } from 'ag-grid-community';
import type { IOvertimeResult } from '../../../models/overtime/OvertimeModel';

export function useOvertimeManagementColumnDefs(
  actionCellRenderer: ComponentType<ICellRendererParams<IOvertimeResult>>,
  statusCellRenderer: ComponentType<ICellRendererParams<IOvertimeResult>>,
  showStatusColumn: boolean
): ColDef<IOvertimeResult>[] {
  return useMemo<ColDef<IOvertimeResult>[]>(
    () => [
      {
        headerName: 'İşlemler',
        width: 160,
        pinned: 'left',
        sortable: false,
        filter: false,
        cellRenderer: actionCellRenderer,
        cellStyle: () => ({
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '0 8px',
          backgroundColor: 'transparent',
        }),
      },
      {
        field: 'id',
        headerName: 'ID',
        width: 80,
        filter: 'agTextColumnFilter',
        filterParams: {
          filterOptions: ['contains'],
          defaultOption: 'contains',
          buttons: ['apply', 'reset'],
          closeOnApply: true,
          suppressAndOrCondition: true,
          maxNumConditions: 1,
        },
      },
      {
        field: 'userName',
        headerName: 'Kullanıcı',
        minWidth: 150,
        flex: 1,
        filter: 'agTextColumnFilter',
        filterParams: {
          filterOptions: ['contains'],
          defaultOption: 'contains',
          buttons: ['apply', 'reset'],
          closeOnApply: true,
          suppressAndOrCondition: true,
          maxNumConditions: 1,
        },
      },
      {
        field: 'startDate',
        headerName: 'Başlangıç Tarihi',
        minWidth: 150,
        filter: 'agTextColumnFilter',
        filterParams: {
          filterOptions: ['contains'],
          defaultOption: 'contains',
          buttons: ['apply', 'reset'],
          closeOnApply: true,
          suppressAndOrCondition: true,
          maxNumConditions: 1,
        },
        valueFormatter: (params: ValueFormatterParams) => {
          if (!params.value) return '';
          return new Date(params.value).toLocaleString('tr-TR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
          });
        },
      },
      {
        field: 'endDate',
        headerName: 'Bitiş Tarihi',
        minWidth: 150,
        filter: 'agTextColumnFilter',
        filterParams: {
          filterOptions: ['contains'],
          defaultOption: 'contains',
          buttons: ['apply', 'reset'],
          closeOnApply: true,
          suppressAndOrCondition: true,
          maxNumConditions: 1,
        },
        valueFormatter: (params: ValueFormatterParams) => {
          if (!params.value) return '';
          return new Date(params.value).toLocaleString('tr-TR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
          });
        },
      },
      {
        field: 'totalHours',
        headerName: 'Toplam Saat',
        width: 120,
        filter: 'agTextColumnFilter',
        filterParams: {
          filterOptions: ['contains'],
          defaultOption: 'contains',
          buttons: ['apply', 'reset'],
          closeOnApply: true,
          suppressAndOrCondition: true,
          maxNumConditions: 1,
        },
      },
      {
        field: 'description',
        headerName: 'Açıklama',
        minWidth: 200,
        flex: 2,
        filter: 'agTextColumnFilter',
        filterParams: {
          filterOptions: ['contains'],
          defaultOption: 'contains',
          buttons: ['apply', 'reset'],
          closeOnApply: true,
          suppressAndOrCondition: true,
          maxNumConditions: 1,
        },
      },
      {
        field: 'overtimeType',
        headerName: 'Tür',
        width: 100,
        filter: 'agTextColumnFilter',
        filterParams: {
          filterOptions: ['contains'],
          defaultOption: 'contains',
          buttons: ['apply', 'reset'],
          closeOnApply: true,
          suppressAndOrCondition: true,
          maxNumConditions: 1,
        },
        valueFormatter: (params: ValueFormatterParams) => {
          return params.value === 'Overtime' ? 'Mesai' : 'İzin';
        },
      },
      {
        field: 'status',
        headerName: 'Durum',
        width: 120,
        filter: 'agTextColumnFilter',
        filterParams: {
          filterOptions: ['contains'],
          defaultOption: 'contains',
          buttons: ['apply', 'reset'],
          closeOnApply: true,
          suppressAndOrCondition: true,
          maxNumConditions: 1,
        },
        cellRenderer: statusCellRenderer,
        hide: !showStatusColumn,
      },
      {
        field: 'createdDate',
        headerName: 'Oluşturulma Tarihi',
        minWidth: 160,
        filter: 'agTextColumnFilter',
        filterParams: {
          filterOptions: ['contains'],
          defaultOption: 'contains',
          buttons: ['apply', 'reset'],
          closeOnApply: true,
          suppressAndOrCondition: true,
          maxNumConditions: 1,
        },
        valueFormatter: (params: ValueFormatterParams) => {
          if (!params.value) return '';
          return new Date(params.value).toLocaleDateString('tr-TR');
        },
      },
      {
        field: 'approvedBy',
        headerName: 'Onaylayan/Reddeden',
        minWidth: 150,
        flex: 1,
        filter: 'agTextColumnFilter',
        filterParams: {
          filterOptions: ['contains'],
          defaultOption: 'contains',
          buttons: ['apply', 'reset'],
          closeOnApply: true,
          suppressAndOrCondition: true,
          maxNumConditions: 1,
        },
      },
    ],
    [actionCellRenderer, showStatusColumn, statusCellRenderer]
  );
}

export function useOvertimeManagementDefaultColDef(): ColDef<IOvertimeResult> {
  return useMemo(
    () => ({
      sortable: true,
      resizable: true,
      suppressMovable: true,
      filter: 'agTextColumnFilter',
      filterParams: {
        filterOptions: ['contains'],
        defaultOption: 'contains',
        buttons: ['apply', 'reset'],
        closeOnApply: true,
        suppressAndOrCondition: true,
        maxNumConditions: 1,
      },
    }),
    []
  );
}
